import { useEffect, useState } from 'react';
import { collection, getCountFromServer, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import type { BadgeSource } from '../../lib/home-schema.js';

const DEMO_MODE = import.meta.env.VITE_SFP_DEMO_MODE === 'true';
const DEMO_COUNTS: Record<BadgeSource, number> = {
  triage: 12,
  bills_due_week: 4,
  bills_past_due: 2,
  documents_total: 47,
};

/**
 * Live counts powering home-page badges and Action Required widget.
 *
 * Uses getCountFromServer so we don't pull whole collections client-side.
 * Failures return 0 — badges absent is preferable to errors blocking the home page.
 */
export function useBadgeCounts() {
  const { user, role } = useAuth();
  const [counts, setCounts] = useState<Record<BadgeSource, number>>(
    DEMO_MODE
      ? DEMO_COUNTS
      : { triage: 0, bills_due_week: 0, bills_past_due: 0, documents_total: 0 },
  );

  useEffect(() => {
    if (DEMO_MODE) return;
    if (!user) return;
    let cancelled = false;
    const isAdmin = role === 'admin';

    (async () => {
      const tasks: Array<Promise<[BadgeSource, number]>> = [];

      if (isAdmin) {
        tasks.push(
          safeCount('triage', query(collection(db, 'documents'), where('status', '==', 'triage'))),
        );
      }

      const now = Timestamp.now();
      const inSevenDays = Timestamp.fromMillis(now.toMillis() + 7 * 24 * 60 * 60 * 1000);
      tasks.push(
        safeCount(
          'bills_due_week',
          query(
            collection(db, 'bills'),
            where('status', 'in', ['received', 'review', 'approved', 'scheduled']),
            where('dueDate', '<=', inSevenDays),
          ),
        ),
      );
      tasks.push(
        safeCount(
          'bills_past_due',
          query(
            collection(db, 'bills'),
            where('status', 'in', ['received', 'review', 'approved', 'scheduled']),
            where('dueDate', '<', now),
          ),
        ),
      );

      const results = await Promise.all(tasks);
      if (cancelled) return;
      const next: Record<BadgeSource, number> = { ...counts };
      for (const [k, v] of results) next[k] = v;
      setCounts(next);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role]);

  return counts;
}

async function safeCount(
  key: BadgeSource,
  q: ReturnType<typeof query>,
): Promise<[BadgeSource, number]> {
  try {
    const snap = await getCountFromServer(q);
    return [key, snap.data().count];
  } catch {
    return [key, 0];
  }
}
